import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner, Button } from '@heroui/react';
import { Award, BadgeCheck, CalendarClock, ClipboardCheck, FileText } from 'lucide-react';
import UserTabs from './components/UserTabs.js';
import ProfileHeader from './components/ProfileHeader.js';
import CredentialSection from './components/CredentialSection.js';
import DocumentsSection from './DocumentsPage.js';
import CertificatesSection from './CertificatesPage.js';
import EndorsementsSection from './EndorsementsPage.js';
import { serverData } from './serverBootstrap.js';
import CredentialsApi from './credentialsApi.js';
import { getOrganizationUnique } from '../../api/utils.js';
import {
	toCertificatesPayload,
	toDocumentsPayload,
	toEndorsementsPayload
} from './credentialsPayload.js';
import { payloadExpiry, renewDelayMs } from './attachments.js';

/**
 * CredentialsPage — Documents, Certificates, Endorsements and the FAA Flight
 * Review as ONE page.
 *
 * Not one page with tabs on it: one scroll, four category blocks, all present
 * at once. The rail's "On this page" list jumps between them and tracks the
 * one you are reading — navigation without hiding anything.
 *
 * Data comes from Symfony (`credentialsApi`), reshaped by `credentialsPayload`
 * into the payload the sections already read. The three groups load in
 * parallel and each block paints the moment ITS data lands, so a slow catalog
 * never holds up the rest.
 *
 * `window.serverData` is still the source for the tabs —
 * that part is rendered by the twig that serves this page. The credential data
 * no longer comes from it, which is what let the HTML-scraping stop-gap go.
 *
 * If a group fails, its block offers the legacy page for that section rather
 * than a dead panel: those routes still exist and still work.
 */
const GROUPS = [
	{
		key: 'documents',
		path: '/users/documents',
		sections: [{ id: 'documents', title: 'Documents', icon: FileText }],
		load: async () => {
			const [documents, types] = await Promise.all([
				CredentialsApi.getDocuments(),
				CredentialsApi.getDocumentTypes()
			]);
			return toDocumentsPayload(documents, types);
		}
	},
	{
		key: 'certificates',
		path: '/users/certificates',
		sections: [{ id: 'certificates', title: 'Certificates', icon: Award }],
		load: async () => {
			const [certificates, metadata] = await Promise.all([
				CredentialsApi.getCertificates(),
				CredentialsApi.getCertificateMetadata()
			]);
			return toCertificatesPayload(certificates, metadata);
		}
	},
	{
		key: 'personalinfo',
		path: '/users/personalinfo',
		sections: [
			{ id: 'endorsements', title: 'Endorsements', icon: ClipboardCheck },
			{ id: 'flight-review', title: 'FAA Flight Review', icon: CalendarClock }
		],
		load: async () => {
			const [endorsement, reviews] = await Promise.all([
				CredentialsApi.getEndorsement(),
				CredentialsApi.getFlightReviews()
			]);
			return toEndorsementsPayload(endorsement, reviews);
		}
	}
];

const ANCHORS = GROUPS.flatMap(g => g.sections);

function CredentialsPage({ initialSection = 'documents' }) {

	const [payloads, setPayloads] = useState({});
	const [failed, setFailed] = useState({});
	const [current, setCurrent] = useState(ANCHORS[0].id);

	const orgUnique = getOrganizationUnique() || '';
	const base = orgUnique ? `/org/${orgUnique}` : '';
	const element = serverData()._element || '';
	const suffix = element ? `/${element}/` : '/';

	const urlFor = useCallback(
		key => `${base}${GROUPS.find(g => g.key === key).path}${suffix}`,
		[base, suffix]
	);

	// All three groups at once — they are independent reads and the user wants
	// the whole page, not the piece they happened to land on.
	useEffect(() => {
		let cancelled = false;
		GROUPS.forEach(g => {
			g.load().then(
				data => {
					if (!cancelled) setPayloads(prev => ({ ...prev, [g.key]: data }));
				},
				error => {
					if (cancelled) return;
					console.error(`Credentials: ${g.key} failed to load`, error);
					setFailed(prev => ({ ...prev, [g.key]: true }));
				}
			);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// Scan links are presigned for 15 minutes. Re-read a group a minute before
	// its earliest link expires, and on returning to a tab whose links lapsed
	// meanwhile (timers stall while a laptop sleeps).
	const payloadsRef = useRef(payloads);
	payloadsRef.current = payloads;
	// Re-reads one group. Also what a section calls after a write, so the page
	// keeps its toast, scroll and open drawer instead of reloading.
	const reloadGroup = useCallback(key => {
		const group = GROUPS.find(g => g.key === key);
		if (!group) return Promise.resolve();
		return group.load().then(data => setPayloads(prev => ({ ...prev, [key]: data })));
	}, []);
	const refreshQuietly = useCallback(
		key =>
			reloadGroup(key).catch(error => console.error(`Credentials: ${key} failed to refresh`, error)),
		[reloadGroup]
	);

	useEffect(() => {
		const timers = Object.keys(payloads)
			.map(key => {
				const delay = renewDelayMs(payloadExpiry(payloads[key]));
				return delay == null ? null : setTimeout(() => refreshQuietly(key), delay);
			})
			.filter(Boolean);
		return () => timers.forEach(clearTimeout);
	}, [payloads, refreshQuietly]);

	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState !== 'visible') return;
			const current = payloadsRef.current;
			Object.keys(current).forEach(key => {
				if (renewDelayMs(payloadExpiry(current[key])) === 0) refreshQuietly(key);
			});
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => document.removeEventListener('visibilitychange', onVisible);
	}, [refreshQuietly]);

	// Highlight the block being read. `rootMargin` biases the hit area to the
	// upper third so the entry that lights up is the one under the header,
	// not whatever happens to touch the viewport's bottom edge.
	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				const visible = entries
					.filter(e => e.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
				if (visible) setCurrent(visible.target.id);
			},
			{ rootMargin: '-96px 0px -60% 0px', threshold: 0 }
		);
		ANCHORS.forEach(a => {
			const el = document.getElementById(a.id);
			if (el) observer.observe(el);
		});
		return () => observer.disconnect();
	}, [payloads, failed]);

	// Old links (/users/certificates, /users/personalinfo) still land here —
	// take them to the block they asked for instead of dropping them at the top.
	const jumped = useRef(false);
	useEffect(() => {
		if (jumped.current || initialSection === GROUPS[0].key) return;
		const target = GROUPS.find(g => g.key === initialSection);
		const el = target && document.getElementById(target.sections[0].id);
		if (!el) return;
		jumped.current = true;
		el.scrollIntoView({ block: 'start' });
	}, [initialSection, payloads]);

	const jump = useCallback((event, id) => {
		event.preventDefault();
		const el = document.getElementById(id);
		if (!el) return;
		el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setCurrent(id);
	}, []);

	const renderGroup = group => {
		const payload = payloads[group.key];

		if (payload) {
			const onRefresh = () => reloadGroup(group.key);
			if (group.key === 'documents')
				return <DocumentsSection key={group.key} payload={payload} onRefresh={onRefresh} />;
			if (group.key === 'certificates')
				return <CertificatesSection key={group.key} payload={payload} onRefresh={onRefresh} />;
			return <EndorsementsSection key={group.key} payload={payload} onRefresh={onRefresh} />;
		}

		// Placeholders keep the anchors resolvable and the page's height honest
		// while a sibling is still in flight.
		return group.sections.map(s => (
			<CredentialSection key={s.id} id={s.id} icon={s.icon} title={s.title}>
				{failed[group.key] ? (
					<div className="flex flex-col items-center gap-3 py-8 text-center">
						<p className="text-sm text-default-500 m-0 max-w-[420px]">
							This section could not be loaded here. It still works on its own page.
						</p>
						<Button as="a" href={urlFor(group.key)} color="primary" radius="sm" size="sm">
							Open {s.title}
						</Button>
					</div>
				) : (
					<div className="flex justify-center py-10">
						<Spinner size="lg" />
					</div>
				)}
			</CredentialSection>
		));
	};

	return (
		<div className="flex flex-col gap-5 py-5">
			<ProfileHeader
				icon={BadgeCheck}
				title="Credentials"
				subtitle="Documents, certificates and endorsements on file"
			/>

			<UserTabs base={base} suffix={suffix} active="credentials" />

			{/* The identity rail lives on Personal Information only; here the side
			    column is just the "On this page" jump list, so the tables get the room. */}
			<div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)] gap-5 items-start">
				{/* `max-lg:hidden` on a wrapper without `flex`: Bootstrap's `.hidden` and
				    the layout's `.flex` are both `!important` and would beat it. */}
				<div className="max-lg:hidden lg:sticky lg:top-[88px]">
					<div className="flex flex-col gap-4">
						<nav
							aria-label="On this page"
							className="flex flex-col gap-1 bg-content1 border border-default-200 rounded-xl p-3"
						>
							<span className="text-[11px] font-semibold uppercase tracking-wide text-default-400 px-2 pb-1">
								On this page
							</span>
							{ANCHORS.map(a => {
								const isCurrent = a.id === current;
								return (
									<a
										key={a.id}
										href={`#${a.id}`}
										onClick={e => jump(e, a.id)}
										aria-current={isCurrent ? 'true' : undefined}
										className={[
											'flex items-center gap-2 h-9 px-2 rounded-md text-[13px] no-underline transition-colors',
											isCurrent
												? 'bg-default-100 font-semibold text-foreground'
												: 'font-medium text-default-500 hover:text-foreground hover:bg-default-50'
										].join(' ')}
									>
										<a.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
										{a.title}
									</a>
								);
							})}
						</nav>
					</div>
				</div>

				<div className="flex flex-col gap-4 min-w-0">{GROUPS.map(renderGroup)}</div>
			</div>
		</div>
	);
}

export default CredentialsPage;
